import Gallery from '@allesauto/core/gallery';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { SchemaTypes } from '@allesauto/graphql';
import { Close24 } from '@carbon/icons-react';
import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';
import React, { FC, Fragment } from 'react';

interface CarGalleryOverlayProps {
  showDialog: boolean;
  onClose: () => void;
  items: SchemaTypes.VehicleImage[] | SchemaTypes.ProductImage[];
  selectedSlide: number;
}

const CarGalleryOverlay: FC<
  React.PropsWithChildren<CarGalleryOverlayProps>
> = ({ showDialog, onClose, items, selectedSlide }) => {
  const carouselClassName = {
    dotListClass:
      '!justify-start md:!pb-5px lg:!pb-5px md:!mb-10px md:!px-0 col-start-1 col-end-25 md:!col-start-2 md:!col-end-24',
    containerClass:
      '!static !pb-0 !mt-0 !col-start-1 !col-end-25 lg:!col-end-23 lg:!col-start-3 md:!col-start-2 md:!col-end-24',
    sectionClass: '!pb-0 !static',
  };

  const whiteLabelIntegrationSlug = useWhiteLabel()?.subdomain || undefined;

  const className = {
    dialogContainerClass: clsx(
      'bg-white fixed grid grid-cols-24 h-full inset-0 w-full z-50',
      whiteLabelIntegrationSlug
        ? `theme-monochrome wl-${whiteLabelIntegrationSlug}`
        : undefined
    ),
    closeButtonContainerClass:
      'absolute flex justify-end md:mr-30px md:mt-30px mt-20px mr-20px right-0',
    closeButtonClass:
      'inline-flex items-center justify-center text-purple80 monochrome:text-black',
    carouselContainerClass: 'col-end-25 col-start-1 self-center',
    pictureContainerClass: 'md:mr-auto ml-auto lg:w-[63%] w-full',
  };

  return (
    <Transition
      enter="transition-opacity duration-300 ease-in"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-300 ease-out"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      show={showDialog}
      as={Fragment}
    >
      <Dialog
        onClose={onClose}
        open={showDialog}
        as="div"
        className={className.dialogContainerClass}
      >
        <div className={className.closeButtonContainerClass}>
          <button
            type="button"
            className={className.closeButtonClass}
            onClick={onClose}
          >
            <Close24 aria-hidden="true" />
          </button>
        </div>
        <Gallery
          items={items}
          selectedSlide={selectedSlide}
          carouselClassNames={carouselClassName}
          pictureClassNames={className.pictureContainerClass}
        />
      </Dialog>
    </Transition>
  );
};

export default CarGalleryOverlay;
